import React, { useState, useEffect, useRef, useContext } from 'react';
import { Alert, Button, Input, Form, message, Typography, Divider } from 'antd';
import { PhoneOutlined, SafetyOutlined } from '@ant-design/icons';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { enrollUserInMFA } from '../../services/Auth/Firebase';
import Loader from '../Loader/Loader';
import { AuthContext } from 'contexts/AuthContext';
import {
  clearRecaptchaVerifier,
  isRecaptchaVerificationError,
  reopenRecaptchaWidget,
  resetRecaptchaWidget,
} from './recaptcha';
import { reportPhoneVerificationError } from './phoneVerificationError';
import {
  hasLifecellIssuedPrefix,
  LIFECELL_WARNING_MESSAGE,
} from './lifecellWarning';

const { Title, Text } = Typography;

interface MandatoryMFAEnrollmentProps {
  onSuccess: () => void;
  onRequiresRecentLogin: () => void;
}

const MandatoryMFAEnrollment: React.FC<MandatoryMFAEnrollmentProps> = ({
  onSuccess,
  onRequiresRecentLogin,
}) => {
  const { currentUser, update } = useContext(AuthContext);
  const [form] = Form.useForm();
  const enteredPhoneNumber = Form.useWatch('phoneNumber', form);
  const [initialLoading, setInitialLoading] = useState(true);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [verifyCodeLoading, setVerifyCodeLoading] = useState(false);
  const [step, setStep] = useState<'email' | 'phone' | 'verification'>('phone');
  const [verificationId, setVerificationId] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [emailResendCooldown, setEmailResendCooldown] = useState<number>(0);
  const [emailResendLoading, setEmailResendLoading] = useState(false);
  const [smsResendCooldown, setSmsResendCooldown] = useState<number>(0);
  const [smsResendLoading, setSmsResendLoading] = useState(false);
  const [emailVerificationSent, setEmailVerificationSent] =
    useState<boolean>(false);
  const [isRecaptchaInitialized, setIsRecaptchaInitialized] = useState(false);
  const [isInitialStepDecided, setIsInitialStepDecided] = useState(false);

  // Refs to store interval IDs for cleanup
  const emailCooldownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const smsCooldownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Refs to store timeout IDs for cleanup
  const recaptchaInitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const enrollUserTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const recaptchaVerifierRef = useRef<firebase.auth.RecaptchaVerifier | null>(
    null,
  );
  const recaptchaWidgetIdRef = useRef<number | null>(null);
  const phoneVerificationInFlightRef = useRef(false);
  const resendVerificationInFlightRef = useRef(false);

  useEffect(() => {
    const decideInitialStep = async () => {
      try {
        if (!currentUser) {
          setIsInitialStepDecided(true);
          return;
        }

        await currentUser.reload();
        if (!currentUser.emailVerified && !emailVerificationSent) {
          setStep('email');
          try {
            const actionCodeSettings = {
              url: `${window.location.origin}/signin`,
            };
            await currentUser.sendEmailVerification(actionCodeSettings);
            setEmailVerificationSent(true);
            // Set initial cooldown to prevent immediate resend
            setEmailResendCooldown(60); // 1 minute initial cooldown

            // Start the initial cooldown timer
            if (emailCooldownIntervalRef.current) {
              clearInterval(emailCooldownIntervalRef.current);
            }

            emailCooldownIntervalRef.current = setInterval(() => {
              setEmailResendCooldown((prev) => {
                if (prev <= 1) {
                  if (emailCooldownIntervalRef.current) {
                    clearInterval(emailCooldownIntervalRef.current);
                    emailCooldownIntervalRef.current = null;
                  }
                  return 0;
                }
                return prev - 1;
              });
            }, 1000);

            message.success(
              'Verification email sent. Please check your inbox.',
            );
          } catch (e: any) {
            console.error('Failed to send verification email:', e);
            if (e.code === 'auth/too-many-requests') {
              setEmailResendCooldown(300); // 5 minutes if rate limited

              // Start the rate limit cooldown timer
              if (emailCooldownIntervalRef.current) {
                clearInterval(emailCooldownIntervalRef.current);
              }

              emailCooldownIntervalRef.current = setInterval(() => {
                setEmailResendCooldown((prev) => {
                  if (prev <= 1) {
                    if (emailCooldownIntervalRef.current) {
                      clearInterval(emailCooldownIntervalRef.current);
                      emailCooldownIntervalRef.current = null;
                    }
                    return 0;
                  }
                  return prev - 1;
                });
              }, 1000);

              message.error(
                'Too many requests. Please wait before requesting another email.',
              );
            } else {
              message.error(
                'Failed to send verification email. Please try again.',
              );
            }
          }
        } else if (!currentUser.emailVerified && emailVerificationSent) {
          setStep('email');
        }
      } catch (e) {
        console.error('Failed to decide initial step:', e);
      } finally {
        setIsInitialStepDecided(true);
      }
    };

    if (!isInitialStepDecided) {
      decideInitialStep();
    }
  }, [currentUser, isInitialStepDecided]);

  useEffect(() => {
    let cancelled = false;

    const initializeRecaptcha = () => {
      if (cancelled) {
        return;
      }

      try {
        const container = document.getElementById(
          'mandatory-mfa-recaptcha-container',
        );
        if (!container) {
          recaptchaInitTimeoutRef.current = setTimeout(
            initializeRecaptcha,
            100,
          );
          return;
        }

        if (!recaptchaVerifierRef.current) {
          clearRecaptchaVerifier(recaptchaVerifierRef.current, container);
          recaptchaVerifierRef.current = new firebase.auth.RecaptchaVerifier(
            'mandatory-mfa-recaptcha-container',
            {
              size: 'invisible',
              badge: 'inline',
              'expired-callback': () => {
                resetRecaptchaWidget(recaptchaWidgetIdRef.current);
                message.error('reCAPTCHA expired. Please try again.');
              },
              'error-callback': () => {
                resetRecaptchaWidget(recaptchaWidgetIdRef.current);
                message.error('reCAPTCHA error. Please try again.');
              },
            },
          );
          recaptchaVerifierRef.current
            .render()
            .then((widgetId) => {
              if (cancelled) {
                return;
              }

              recaptchaWidgetIdRef.current = widgetId;
              setIsRecaptchaInitialized(true);
            })
            .catch((error: any) => {
              if (cancelled) {
                return;
              }

              console.error('Failed to render reCAPTCHA:', error);
              message.error(
                'Failed to initialize reCAPTCHA. Please refresh the page.',
              );
            });
        }
      } catch (e) {
        console.error('Failed to initialize reCAPTCHA:', e);
        message.error(
          'Failed to initialize reCAPTCHA. Please refresh the page.',
        );
      }
    };

    recaptchaInitTimeoutRef.current = setTimeout(initializeRecaptcha, 100);

    return () => {
      cancelled = true;
      clearRecaptchaVerifier(
        recaptchaVerifierRef.current,
        document.getElementById('mandatory-mfa-recaptcha-container'),
      );
      recaptchaVerifierRef.current = null;
      recaptchaWidgetIdRef.current = null;

      // Clean up intervals
      if (emailCooldownIntervalRef.current) {
        clearInterval(emailCooldownIntervalRef.current);
      }
      if (smsCooldownIntervalRef.current) {
        clearInterval(smsCooldownIntervalRef.current);
      }

      // Clean up all timeouts
      if (recaptchaInitTimeoutRef.current) {
        clearTimeout(recaptchaInitTimeoutRef.current);
      }
      if (enrollUserTimeoutRef.current) {
        clearTimeout(enrollUserTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isRecaptchaInitialized && isInitialStepDecided) {
      setInitialLoading(false);
    }
  }, [isRecaptchaInitialized, isInitialStepDecided]);

  const resendVerificationEmail = async () => {
    if (emailResendCooldown > 0) {
      message.warning(
        `Please wait ${emailResendCooldown} seconds before requesting another email.`,
      );
      return;
    }

    setEmailResendLoading(true);
    try {
      if (!currentUser) {
        message.error('User not found. Please refresh the page.');
        return;
      }

      const actionCodeSettings = {
        url: `${window.location.origin}/signin`,
        handleCodeInApp: true,
      };
      await currentUser.sendEmailVerification(actionCodeSettings);
      message.success('Verification email sent. Please check your inbox.');

      // Start 2-minute cooldown (120 seconds) for resend
      setEmailResendCooldown(120);

      // Clear any existing interval
      if (emailCooldownIntervalRef.current) {
        clearInterval(emailCooldownIntervalRef.current);
      }

      emailCooldownIntervalRef.current = setInterval(() => {
        setEmailResendCooldown((prev) => {
          if (prev <= 1) {
            if (emailCooldownIntervalRef.current) {
              clearInterval(emailCooldownIntervalRef.current);
              emailCooldownIntervalRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      console.error('Email verification error:', error);

      if (error.code === 'auth/too-many-requests') {
        message.error(
          'Too many requests. Please wait 5 minutes before trying again.',
        );
        // Set a longer cooldown for rate limit errors
        setEmailResendCooldown(300);

        // Clear any existing interval
        if (emailCooldownIntervalRef.current) {
          clearInterval(emailCooldownIntervalRef.current);
        }

        emailCooldownIntervalRef.current = setInterval(() => {
          setEmailResendCooldown((prev) => {
            if (prev <= 1) {
              if (emailCooldownIntervalRef.current) {
                clearInterval(emailCooldownIntervalRef.current);
                emailCooldownIntervalRef.current = null;
              }
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else if (error.code === 'auth/user-not-found') {
        message.error('User not found. Please refresh the page and try again.');
      } else if (error.code === 'auth/invalid-email') {
        message.error('Invalid email address. Please contact support.');
      } else if (error.code === 'auth/requires-recent-login') {
        onRequiresRecentLogin();
      } else {
        message.error(
          error.message ||
            'Failed to send verification email. Please try again later.',
        );
      }
    } finally {
      setEmailResendLoading(false);
    }
  };

  const recheckEmailVerified = async () => {
    try {
      if (!currentUser) return;
      await currentUser.reload();
      if (currentUser.emailVerified) {
        setStep('phone');
        message.success('Email verified. Continue with phone setup.');
      } else {
        message.warning('Email is still unverified. Please check your inbox.');
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to check verification status');
    }
  };

  const handlePhoneSubmit = async (values: { phoneNumber: string }) => {
    if (phoneVerificationInFlightRef.current) {
      return;
    }

    phoneVerificationInFlightRef.current = true;
    setPhoneLoading(true);
    try {
      let formattedPhone = values.phoneNumber.replace(/[^\d+]/g, '');

      if (!formattedPhone.startsWith('+')) {
        formattedPhone = `+1${formattedPhone}`;
      }

      const digitsOnly = formattedPhone.replace(/[^\d]/g, '');
      if (digitsOnly.length < 10) {
        throw new Error('Phone number must have at least 10 digits');
      }

      setPhoneNumber(formattedPhone);

      if (!currentUser) {
        throw new Error('User must be signed in to enroll in MFA');
      }

      if (!currentUser.emailVerified) {
        setStep('email');
        throw new Error(
          'Please verify your email before enabling MFA. Open the email we sent and click the link, then return here and continue.',
        );
      }

      if (!recaptchaVerifierRef.current) {
        throw new Error('reCAPTCHA not initialized. Please refresh the page.');
      }

      // Always discard the previous attempt's token before verifying a phone
      // number. Firebase can leave it unusable after an upstream carrier error.
      resetRecaptchaWidget(recaptchaWidgetIdRef.current);

      const mfaSession = await currentUser.multiFactor.getSession();
      if (!mfaSession) {
        throw new Error('Failed to get MFA session. Please try again.');
      }

      const phoneProvider = new firebase.auth.PhoneAuthProvider();

      const verificationId = await phoneProvider.verifyPhoneNumber(
        {
          phoneNumber: formattedPhone,
          session: mfaSession,
        },
        recaptchaVerifierRef.current,
      );

      setVerificationId(verificationId);
      setStep('verification');
      message.success('Verification code sent to your phone');
    } catch (error: any) {
      console.error('Phone verification error:', error);
      resetRecaptchaWidget(recaptchaWidgetIdRef.current);

      if (isRecaptchaVerificationError(error)) {
        message.error(
          'Security verification expired or was rejected. Try sending the code again; a reCAPTCHA challenge may appear.',
        );
      } else if (error.code === 'auth/requires-recent-login') {
        onRequiresRecentLogin();
      } else {
        const reportedError = reportPhoneVerificationError(
          error,
          'mfa-enrollment-start',
          {
            suspectedLifecellPrefix: hasLifecellIssuedPrefix(
              values.phoneNumber,
            ),
          },
        );
        if (reportedError) {
          message.error(
            `${reportedError.userMessage} Incident ID: ${reportedError.incidentId}`,
            10,
          );
        } else {
          message.error(error.message || 'Failed to send verification code');
        }
      }
    } finally {
      phoneVerificationInFlightRef.current = false;
      setPhoneLoading(false);
    }
  };

  const handleReopenRecaptcha = () => {
    if (!reopenRecaptchaWidget(recaptchaWidgetIdRef.current)) {
      message.error(
        'Unable to reopen the security check. Please refresh the page.',
      );
    }
  };

  const handleVerificationSubmit = async (values: {
    verificationCode: string;
  }) => {
    setVerifyCodeLoading(true);
    try {
      const updatedUser = await enrollUserInMFA(
        verificationId,
        values.verificationCode,
      );

      update(updatedUser);

      message.success('Multi-factor authentication enabled successfully!');

      enrollUserTimeoutRef.current = setTimeout(() => {
        onSuccess();
      }, 100);
    } catch (error: any) {
      if (error.code === 'auth/invalid-verification-code') {
        message.error('Incorrect SMS code! Please try again.');
      } else if (error.code === 'auth/requires-recent-login') {
        onRequiresRecentLogin();
      } else {
        const reportedError = reportPhoneVerificationError(
          error,
          'mfa-enrollment-finalize',
        );
        if (reportedError) {
          message.error(
            `${reportedError.userMessage} Incident ID: ${reportedError.incidentId}`,
            10,
          );
        } else {
          message.error(
            error.message || 'Failed to enable multi-factor authentication',
          );
        }
      }
    } finally {
      setVerifyCodeLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendVerificationInFlightRef.current) {
      return;
    }

    if (smsResendCooldown > 0) {
      message.warning(
        `Please wait ${smsResendCooldown} seconds before requesting another SMS code.`,
      );
      return;
    }

    resendVerificationInFlightRef.current = true;
    setSmsResendLoading(true);
    try {
      resetRecaptchaWidget(recaptchaWidgetIdRef.current);

      if (!currentUser) {
        throw new Error('User must be signed in to enroll in MFA');
      }

      if (!recaptchaVerifierRef.current) {
        throw new Error('reCAPTCHA not initialized. Please refresh the page.');
      }

      const mfaSession = await currentUser.multiFactor.getSession();
      if (!mfaSession) {
        throw new Error('Failed to get MFA session. Please try again.');
      }

      const phoneProvider = new firebase.auth.PhoneAuthProvider();

      const verificationId = await phoneProvider.verifyPhoneNumber(
        {
          phoneNumber: phoneNumber,
          session: mfaSession,
        },
        recaptchaVerifierRef.current,
      );

      setVerificationId(verificationId);
      message.success('Verification code sent to your phone');

      // Start 2-minute cooldown (120 seconds)
      setSmsResendCooldown(120);

      // Clear any existing interval
      if (smsCooldownIntervalRef.current) {
        clearInterval(smsCooldownIntervalRef.current);
      }

      smsCooldownIntervalRef.current = setInterval(() => {
        setSmsResendCooldown((prev) => {
          if (prev <= 1) {
            if (smsCooldownIntervalRef.current) {
              clearInterval(smsCooldownIntervalRef.current);
              smsCooldownIntervalRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      console.error('Phone verification error:', error);
      resetRecaptchaWidget(recaptchaWidgetIdRef.current);

      if (isRecaptchaVerificationError(error)) {
        message.error(
          'Security verification expired or was rejected. Try sending the code again; a reCAPTCHA challenge may appear.',
        );
      } else if (error.code === 'auth/too-many-requests') {
        message.error(
          'Too many requests. Please wait 2 minutes before trying again.',
        );
        // Set a longer cooldown for rate limit errors
        setSmsResendCooldown(120);

        // Clear any existing interval
        if (smsCooldownIntervalRef.current) {
          clearInterval(smsCooldownIntervalRef.current);
        }

        smsCooldownIntervalRef.current = setInterval(() => {
          setSmsResendCooldown((prev) => {
            if (prev <= 1) {
              if (smsCooldownIntervalRef.current) {
                clearInterval(smsCooldownIntervalRef.current);
                smsCooldownIntervalRef.current = null;
              }
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else if (error.code === 'auth/requires-recent-login') {
        onRequiresRecentLogin();
      } else {
        const reportedError = reportPhoneVerificationError(
          error,
          'mfa-enrollment-resend',
          {
            suspectedLifecellPrefix: hasLifecellIssuedPrefix(phoneNumber),
          },
        );
        if (reportedError) {
          message.error(
            `${reportedError.userMessage} Incident ID: ${reportedError.incidentId}`,
            10,
          );
        } else {
          message.error(error.message || 'Failed to resend verification code');
        }
      }
    } finally {
      resendVerificationInFlightRef.current = false;
      setSmsResendLoading(false);
    }
  };

  return (
    <div>
      {initialLoading ? (
        <Loader />
      ) : (
        <div style={{ maxWidth: 500, margin: '0 auto', padding: '20px' }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <Title level={3}>Multi-Factor Authentication Required</Title>
            <Text type="secondary">
              Please enable multifactor authentication on your mobile device to
              help protect your data.
            </Text>
          </div>
          {step === 'email' && (
            <>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <Title level={4}>Verify Your Email</Title>
                <Text type="secondary">
                  Please verify your email address before enabling MFA. Check
                  your inbox for a verification email, then click the button
                  below to re-check.
                </Text>
              </div>

              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <Button
                  onClick={resendVerificationEmail}
                  loading={emailResendLoading}
                  disabled={emailResendCooldown > 0}
                  block
                >
                  {emailResendCooldown > 0
                    ? `Resend email (${Math.floor(emailResendCooldown / 60)}:${(
                        emailResendCooldown % 60
                      )
                        .toString()
                        .padStart(2, '0')})`
                    : 'Resend verification email'}
                </Button>
                <Button type="primary" onClick={recheckEmailVerified} block>
                  I verified — check again
                </Button>
              </div>

              <Divider />
            </>
          )}

          {step === 'phone' && (
            <>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <PhoneOutlined
                  style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }}
                />
                <Title level={4}>Add Your Phone Number</Title>
                <Text type="secondary">
                  We&apos;ll send a verification code to secure your account.
                </Text>
              </div>

              <Form
                form={form}
                onFinish={handlePhoneSubmit}
                layout="vertical"
                requiredMark={false}
              >
                <Form.Item
                  name="phoneNumber"
                  label="Phone Number"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter your phone number',
                    },
                    {
                      pattern: /^\+?[1-9][\d\s\-()]{6,20}$/,
                      message:
                        'Please enter a valid phone number (e.g., +1 (555) 123-4567)',
                    },
                  ]}
                >
                  <Input
                    placeholder="+1 (555) 123-4567"
                    prefix={<PhoneOutlined />}
                    disabled={phoneLoading || !isRecaptchaInitialized}
                  />
                </Form.Item>

                {hasLifecellIssuedPrefix(enteredPhoneNumber) && (
                  <Alert
                    type="warning"
                    showIcon
                    message={LIFECELL_WARNING_MESSAGE}
                    style={{ marginBottom: 16 }}
                  />
                )}

                <Form.Item style={{ marginBottom: 16 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={phoneLoading}
                    disabled={phoneLoading || !isRecaptchaInitialized}
                    block
                    size="large"
                  >
                    Send Verification Code
                  </Button>
                </Form.Item>
                {phoneLoading && (
                  <Form.Item style={{ textAlign: 'center' }}>
                    <Button type="link" onClick={handleReopenRecaptcha}>
                      Reopen security check
                    </Button>
                  </Form.Item>
                )}
              </Form>
            </>
          )}

          {step === 'verification' && (
            <>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <SafetyOutlined
                  style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }}
                />
                <Title level={4}>Verify Your Phone Number</Title>
                <Text type="secondary">
                  Enter the 6-digit code sent to {phoneNumber}
                </Text>
              </div>

              {hasLifecellIssuedPrefix(phoneNumber) && (
                <Alert
                  type="warning"
                  showIcon
                  message={LIFECELL_WARNING_MESSAGE}
                  style={{ marginBottom: 16 }}
                />
              )}

              <Form
                form={form}
                onFinish={handleVerificationSubmit}
                layout="vertical"
                requiredMark={false}
              >
                <Form.Item
                  name="verificationCode"
                  label="Verification Code"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter the verification code',
                    },
                    {
                      pattern: /^\d{6}$/,
                      message: 'Please enter a valid 6-digit code',
                    },
                  ]}
                >
                  <Input
                    placeholder="123456"
                    maxLength={6}
                    disabled={verifyCodeLoading}
                    style={{
                      textAlign: 'center',
                      fontSize: 18,
                      letterSpacing: 4,
                    }}
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 16 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={verifyCodeLoading}
                    disabled={verifyCodeLoading}
                    block
                    size="large"
                  >
                    Enable MFA & Continue
                  </Button>
                </Form.Item>
              </Form>

              <Divider />

              <div
                style={{
                  textAlign: 'center',
                  display: 'flex',
                  gap: 16,
                  justifyContent: 'center',
                }}
              >
                <Button
                  type="link"
                  onClick={handleResendCode}
                  disabled={verifyCodeLoading || smsResendLoading}
                  loading={smsResendLoading}
                >
                  {smsResendCooldown > 0
                    ? `Resend code (${Math.floor(smsResendCooldown / 60)}:${(
                        smsResendCooldown % 60
                      )
                        .toString()
                        .padStart(2, '0')})`
                    : 'Resend verification code'}
                </Button>
                <Button
                  type="link"
                  onClick={() => setStep('phone')}
                  disabled={verifyCodeLoading || smsResendLoading}
                >
                  Change phone number
                </Button>
                {smsResendLoading && (
                  <Button type="link" onClick={handleReopenRecaptcha}>
                    Reopen security check
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      )}
      <Text className="recaptcha-disclosure" type="secondary">
        This site is protected by reCAPTCHA and the Google{' '}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noreferrer"
        >
          Privacy Policy
        </a>{' '}
        and{' '}
        <a
          href="https://policies.google.com/terms"
          target="_blank"
          rel="noreferrer"
        >
          Terms of Service
        </a>{' '}
        apply.
      </Text>
      {/* Keep Google-owned widget DOM scoped to this component. */}
      <div
        id="mandatory-mfa-recaptcha-container"
        className="recaptcha-container"
      ></div>
    </div>
  );
};

export default MandatoryMFAEnrollment;
